import React from 'react';
import PropTypes from 'prop-types';

import OutputText from '@ey/canvascoreservices/OutputText';

import Checkbox from '../Checkbox';

import StyledSelectWithCheckbox from './StyledSelectWithCheckbox';

const defaultLabels = {
	allSelected: 'All selected',
	multiSelect: '{0} selected',
	nonSelected: ''
};

const SelectWithCheckbox = ({
	value = null,
	options = [],
	onChange = null,
	labels = defaultLabels,
	label = null,
	disabled = false,
	...rest
}) => {
	const dummyValue = {value: -1, label: ''};

	const getIsChecked = (itemValue) => !!value?.some((x) => x?.value === itemValue);

	const hasGroups = options.some(({options}) => !!options);

	const getOptionsLength = () => {
		if (hasGroups) {
			const flatOptions = [];
			options.forEach(({options}) => flatOptions.push(...options));
			return flatOptions.length;
		}

		return options.length;
	};

	const handleChange = (newValue) => {
		const isChecked = getIsChecked(newValue.value);
		let result;
		if (isChecked) {
			result = value.filter((x) => x.value !== newValue.value);
		} else {
			const oldValue = value ?? [];
			result = [...oldValue, newValue];
		}

		onChange && onChange(result);
	};

	const renderDummyLabel = () => {
		const length = value?.length;

		let text = labels.nonSelected;
		if (length > 1) text = labels.multiSelect.replace('{0}', length);
		if (length === getOptionsLength()) text = labels.allSelected;
		if (length === 1) text = value[0].label;

		return (
			<OutputText body2 light title={text} ellipsis>
				{text}
			</OutputText>
		);
	};

	const renderOption = ({value: itemValue, label, disabled = false}) => {
		if (itemValue === -1) return renderDummyLabel();

		const isChecked = getIsChecked(itemValue);

		const props = {
			label,
			checked: isChecked,
			disabled
		};

		return <Checkbox {...props} />;
	};

	const props = {
		options,
		value,
		isMulti: true,
		isSearchable: false,
		formatOptionLabel: renderOption,
		defaultValue: value === null ? null : dummyValue,
		value: value === null ? null : dummyValue,
		formLabel: labels.formLabel,
		onChange: (newValue) => handleChange(newValue.filter((x) => x.value !== -1)[0]),
		isDisabled: disabled,
		...rest
	};

	if (label) props.formLabel = label;

	return <StyledSelectWithCheckbox {...props} />;
};

export const getLabelPropTypes = (labels) => {
	const newLabels = {};
	Object.keys(labels).forEach((key) => (newLabels[key] = PropTypes.string.isRequired));
	return newLabels;
};

const optionShape = {
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	label: PropTypes.string.isRequired,
	disabled: PropTypes.bool
};

const groupShape = {
	label: PropTypes.string.isRequired,
	options: PropTypes.shape(optionShape)
};

SelectWithCheckbox.propTypes = {
	value: PropTypes.arrayOf(PropTypes.shape(optionShape)),
	options: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.shape(optionShape)),
		PropTypes.arrayOf(PropTypes.shape(groupShape))
	]),
	onChange: PropTypes.func,
	labels: getLabelPropTypes(defaultLabels),
	disabled: PropTypes.bool
};

export default SelectWithCheckbox;
